import { useTranslations } from "@/lib/useTranslations";
import { useCallback, useEffect, useId, useRef, useState } from "react";

import {
  fetchGooglePlaceDetails,
  searchGooglePlaces,
} from "@/api/google-places";
import { FieldLabel } from "@/components/ui/FieldLabel";
import type {
  GooglePlaceDetails,
  GooglePlaceSuggestion,
} from "@/core/services/types/google-places.types";

const inputClassName =
  "w-full wireframe-border bg-whart-bg px-3 py-2 text-sm text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

function CopyIcon() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 16 16"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
      <path d="M10.5 5.5V3.5A1 1 0 0 0 9.5 2.5h-6a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2" />
    </svg>
  );
}

interface PlaceGoogleSearchFieldProps {
  disabled: boolean;
  required?: boolean;
  googlePlaceId: string;
  selectedLabel: string | null;
  mapsUrl: string | null;
  fieldError?: string;
  onSelect: (details: GooglePlaceDetails) => void;
  onClear: () => void;
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-whart-urgent">{message}</p>;
}

export function PlaceGoogleSearchField({
  disabled,
  required = false,
  googlePlaceId,
  selectedLabel,
  mapsUrl,
  fieldError,
  onSelect,
  onClear,
}: PlaceGoogleSearchFieldProps) {
  const t = useTranslations("places.edit");
  const tSearch = useTranslations("places.edit.googleSearch");
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [isEditing, setIsEditing] = useState(
    !googlePlaceId.trim() && !selectedLabel?.trim(),
  );
  const [suggestions, setSuggestions] = useState<GooglePlaceSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (!copyFeedback) return;
    const timer = window.setTimeout(() => setCopyFeedback(null), 2000);
    return () => window.clearTimeout(timer);
  }, [copyFeedback]);

  async function handleCopyPlaceId() {
    const value = googlePlaceId.trim();
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopyFeedback(t("googlePlaceIdCopied"));
    } catch {
      setCopyFeedback(t("googlePlaceIdCopyFailed"));
    }
  }

  useEffect(() => {
    if (!isEditing) return;

    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setIsSearching(false);
      setSearchError(null);
      return;
    }

    setIsSearching(true);
    const timer = window.setTimeout(async () => {
      const result = await searchGooglePlaces(trimmed);
      setIsSearching(false);

      if (!result.success) {
        setSearchError(result.error);
        setSuggestions([]);
        setIsOpen(false);
        return;
      }

      setSearchError(null);
      setSuggestions(result.suggestions);
      setIsOpen(result.suggestions.length > 0);
      setActiveIndex(-1);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [query, isEditing]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    async (suggestion: GooglePlaceSuggestion) => {
      setIsLoadingDetails(true);
      setSearchError(null);

      const result = await fetchGooglePlaceDetails(suggestion.placeId);
      setIsLoadingDetails(false);

      if (!result.success) {
        setSearchError(result.error);
        return;
      }

      onSelect(result.place);
      setQuery("");
      setSuggestions([]);
      setIsOpen(false);
      setIsEditing(false);
      setActiveIndex(-1);
    },
    [onSelect],
  );

  function handleClear() {
    onClear();
    setQuery("");
    setSuggestions([]);
    setSearchError(null);
    setIsOpen(false);
    setIsEditing(true);
    setActiveIndex(-1);
  }

  function handleChangeSelection() {
    setQuery(selectedLabel ?? "");
    setIsEditing(true);
    setIsOpen(false);
    setActiveIndex(-1);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!isOpen || suggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) =>
        current < suggestions.length - 1 ? current + 1 : 0,
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) =>
        current > 0 ? current - 1 : suggestions.length - 1,
      );
      return;
    }

    if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      const suggestion = suggestions[activeIndex];
      if (suggestion) {
        void handleSelect(suggestion);
      }
      return;
    }

    if (event.key === "Escape") {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  }

  const showDropdown = isOpen && suggestions.length > 0 && !isLoadingDetails;
  const hasSelection = Boolean(googlePlaceId.trim() || selectedLabel?.trim());

  if (!isEditing && hasSelection) {
    return (
      <section className="space-y-2" ref={containerRef}>
        <FieldLabel as="p" required={required}>
          {t("googlePlaceLabel")}
        </FieldLabel>
        <div className="wireframe-border bg-whart-bg px-3 py-2 text-sm text-whart-text">
          {selectedLabel?.trim() || googlePlaceId}
        </div>
        {googlePlaceId.trim() ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <p className="min-w-0 flex-1 truncate font-mono text-xs text-neutral-600">
                <span className="mr-2 font-sans font-medium text-neutral-500">
                  {t("googlePlaceIdLabel")}
                </span>
                {googlePlaceId.trim()}
              </p>
              <button
                type="button"
                onClick={() => void handleCopyPlaceId()}
                disabled={disabled}
                className="inline-flex size-8 shrink-0 items-center justify-center rounded-xl text-neutral-500 transition-colors hover:bg-zinc-50 hover:text-whart-text disabled:opacity-50"
                aria-label={t("copyGooglePlaceId")}
                title={t("copyGooglePlaceId")}
              >
                <CopyIcon />
              </button>
            </div>
            {copyFeedback ? (
              <p className="text-xs text-neutral-500" role="status">
                {copyFeedback}
              </p>
            ) : null}
          </div>
        ) : null}
        {mapsUrl ? (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-xs text-neutral-600 underline hover:text-whart-text"
          >
            {t("googlePlaceOpen")}
          </a>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleChangeSelection}
            disabled={disabled}
            className="text-xs text-neutral-600 underline hover:text-whart-text disabled:opacity-50"
          >
            {tSearch("changeSelection")}
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className="text-xs text-neutral-600 underline hover:text-whart-text disabled:opacity-50"
          >
            {tSearch("clearSelection")}
          </button>
        </div>
        <FieldError message={fieldError} />
      </section>
    );
  }

  return (
    <section className="relative space-y-2" ref={containerRef}>
      <FieldLabel htmlFor="place-google-search" required={required}>
        {t("googlePlaceLabel")}
      </FieldLabel>
      <input
        id="place-google-search"
        type="search"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => {
          if (suggestions.length > 0) setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder={tSearch("placeholder")}
        disabled={disabled || isLoadingDetails}
        autoComplete="off"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
        }
        className={inputClassName}
      />
      {isSearching || isLoadingDetails ? (
        <p className="text-xs text-neutral-500">
          {isLoadingDetails ? tSearch("loadingDetails") : tSearch("searching")}
        </p>
      ) : null}
      {!isSearching && query.trim().length > 0 && query.trim().length < 2 ? (
        <p className="text-xs text-neutral-500">{tSearch("selectHint")}</p>
      ) : null}
      {!isSearching &&
      query.trim().length >= 2 &&
      suggestions.length === 0 &&
      !searchError ? (
        <p className="text-xs text-neutral-500">{tSearch("noResults")}</p>
      ) : null}
      {searchError ? (
        <p role="alert" className="text-xs text-whart-urgent">
          {searchError}
        </p>
      ) : null}
      {showDropdown ? (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto border border-black/10 bg-white shadow-sm"
        >
          {suggestions.map((suggestion, index) => (
            <li key={suggestion.placeId} role="presentation">
              <button
                id={`${listboxId}-option-${index}`}
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => void handleSelect(suggestion)}
                className={`w-full px-3 py-2 text-left text-sm transition-colors hover:bg-neutral-50 ${
                  index === activeIndex ? "bg-neutral-50" : ""
                }`}
              >
                <span className="block font-medium text-whart-text">
                  {suggestion.mainText || suggestion.label}
                </span>
                {suggestion.secondaryText ? (
                  <span className="block text-xs text-neutral-500">
                    {suggestion.secondaryText}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <FieldError message={fieldError} />
    </section>
  );
}
